import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IonicModule, LoadingController, ModalController, NavController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import {
  ActionDropdownComponent,
  ActionDropdownType,
} from 'src/app/components/action-dropdown/action-dropdown.component';
import { IntegrationInputModalComponent } from 'src/app/components/integration-input-modal/integration-input-modal';
import { IntegrationOutputModalComponent } from 'src/app/components/integration-output-modal/integration-output-modal';
import { FEATURES, INTEGRATIONS, INTEGRATION_TYPES, INTEGRATION_TYPE_NAMES } from 'src/app/constants';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { NangoService } from 'src/services/nango.service';

import { AdminAppsRoutingModule } from './admin-apps-routing.module';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, AdminAppsRoutingModule, SharedPipesModule, NgxTippyModule],
  selector: 'app-admin-apps',
  templateUrl: './admin-apps.component.html',
  styleUrls: ['./admin-apps.component.scss'],
})
export class AdminAppsComponent implements OnInit {
  apps: any[] = [];
  isSavingIntegration = false;
  categories = ['All', ..._.values(INTEGRATION_TYPES)];
  categoryNames = INTEGRATION_TYPE_NAMES;
  category = this.categories[0];
  crmFeatureEnabled: boolean;
  taskFeatureEnabled: boolean;
  showInstalledOnly = false;

  constructor(
    private route: ActivatedRoute,
    public authService: AuthService,
    private navCtrl: NavController,
    private msgSrvc: MessageService,
    private loadingCtrl: LoadingController,
    private apiService: ApiService,
    private modalCtrl: ModalController,
    private popoverCtrl: PopoverController,
    private nangoService: NangoService,
  ) {}

  ngOnInit() {
    const projectId = this.route.snapshot.paramMap.get('projectId');

    // If in project-specific view, show Ticketing, Communication, Knowledge Base, and Automation tabs
    if (projectId) {
      this.categories = [
        'All',
        INTEGRATION_TYPES.TICKETING,
        INTEGRATION_TYPES.KNOWLEDGEBASE,
        INTEGRATION_TYPES.AUTOMATION,
      ];
      this.category = this.categories[0];
    } else {
      const queryParamTab = this.route.snapshot.queryParamMap.get('type');
      this.categories.forEach((cat) => {
        if (cat === queryParamTab) {
          this.category = cat;
        }
      });
    }

    this.load();
  }

  load() {
    const tenant = this.authService.tenant;
    const projectId = this.route.snapshot.paramMap.get('projectId');

    this.crmFeatureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.CRM_INTEGRATIONS);
    this.taskFeatureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.EXTERNAL_TASK_MANAGERS);

    this.apps = [];

    Object.keys(INTEGRATIONS).forEach((key) => {
      const app = INTEGRATIONS[key];

      // If projectId is specified, show project-specific integrations and specific communication/knowledge base/automation apps
      if (projectId) {
        // Allow communication apps (Email, In-app Chat, Teams, Slack), knowledge base apps, and automation apps (Zapier, Make, Webhooks)
        const isAllowedCommunicationApp =
          app.type === INTEGRATION_TYPES.COMMUNICATION &&
          (app.key === 'email' || app.key === 'widget' || app.key === 'microsoft-teams' || app.key === 'slack');
        const isKnowledgeBaseApp = app.type === INTEGRATION_TYPES.KNOWLEDGEBASE;
        const isAllowedAutomationApp =
          app.type === INTEGRATION_TYPES.AUTOMATION &&
          (app.key === 'zapier' || app.key === 'make' || app.key === 'webhooks');

        if (!app.isProjectSpecific && !isAllowedCommunicationApp && !isKnowledgeBaseApp && !isAllowedAutomationApp) {
          return;
        }
      }

      const obj = {
        ...app,
        isGlobal: false,
        enabled: _.get(tenant, `integrations.${app.key}`) !== 'disabled',
      };

      if (this.category === 'All' || app.type === this.category) {
        // Apply installed filter if enabled
        if (this.showInstalledOnly) {
          const isInstalled = obj.installable && tenant.integrations[app.key]?.enabled;
          if (isInstalled) {
            this.apps.push(obj);
          }
        } else {
          this.apps.push(obj);
        }
      }
    });

    // Sort integrations alphabetically by name
    this.apps.sort((a, b) => a.name.localeCompare(b.name));
  }

  viewIntegration(app: any) {
    const root = `dashboard/settings`;
    let url = app.url;
    const projectId = this.route.snapshot.paramMap.get('projectId');
    const queryParams: any = {};

    // URL to these is now dynamic depending on the project
    if (app.key === 'email' || app.key === 'widget' || app.key === 'microsoft-teams') {
      const navUrl = `dashboard/channels/${url}`;

      return this.navCtrl.navigateRoot(navUrl, {
        queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      });
    }

    // knowledge base related integrations have a different root
    if (app.type === INTEGRATION_TYPES.KNOWLEDGEBASE) {
      return this.navCtrl.navigateRoot(`dashboard/knowledge/sources`, {
        queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
      });
    }

    if (app.type === INTEGRATION_TYPES.AUTOMATION && projectId) {
      return this.navCtrl.navigateRoot(`${root}/projects/${projectId}/service-accounts`);
    }

    // For JSM, navigate to project-specific page if projectId exists
    if (app.key === 'jsm' && projectId) {
      return this.navCtrl.navigateRoot(`${root}/projects/${projectId}/jsm`);
    }

    this.navCtrl.navigateRoot(`${root}/${url}`, {
      queryParams: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    });
  }

  async reauthorizeApp(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};
    this.isSavingIntegration = true;

    try {
      const nangoConfig = currentIntegrationSettings.nangoConfig;
      const nangoIntegrationId = currentIntegrationSettings.nangoIntegrationId ?? app.key;

      const { connectionId } = await this.nangoService.authorize(
        nangoIntegrationId,
        nangoConfig,
        undefined,
        app.authorizationParams,
      );

      // Call backend to update the integration (including resetting consecutiveAuthFailures)
      await this.apiService.postPromise(`/integrations/register`, {
        integration: nangoIntegrationId,
        integrationType: app.type,
        isNango: app.isNango,
        connectionId,
        params: currentIntegrationSettings.userInput ?? {},
        secrets: {},
        projectId: app.isProjectSpecific ? this.authService.selectedProject?.id ?? null : undefined,
      });

      await this.authService.loadCompany();

      this.msgSrvc.show('Success!');
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
        },
      });

      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }

  async requestAccess(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};

    if (app.registerIntegration) {
      const modal = await this.modalCtrl.create({
        component: IntegrationInputModalComponent,
        componentProps: {
          integration: app,
          integrationSettings: currentIntegrationSettings,
        },
      });
      await modal.present();

      const { data } = await modal.onWillDismiss();
      if (data) {
        await this.authService.loadCompany();
        this.load();

        if (data.integrationOutput && data.integrationOutput.length > 0) {
          const output = await this.modalCtrl.create({
            component: IntegrationOutputModalComponent,
            componentProps: {
              integration: app,
              integrationOutput: data.integrationOutput,
            },
          });
          await output.present();
        }
      }
    } else if (currentIntegrationSettings?.mergeLink) {
      window.open(currentIntegrationSettings.mergeLink, '_blank');
    } else {
      window.open(`mailto:sales@unthread.io?subject=Beta+Access+to+${app.name}+Integration`, '_blank');
    }
  }

  async save() {
    const loader = await this.loadingCtrl.create({
      message: 'Updating dashboard...',
      spinner: 'crescent',
    });

    loader.present();

    // Only some services support global enablement via a service account
    // 'individual' means someone has to oauth their own personal account
    // Hopefully we expand this sometime soon
    const mapping = {
      [INTEGRATIONS.slack.key]: 'individual',
    };
    const integrations: any = {};

    this.apps.map((app) => {
      integrations[app.key] = app.enabled ? mapping[app.key] : 'disabled';
    });

    const updates = {
      integrations,
    };

    try {
      await this.authService.updateTenant(updates);

      this.load();

      loader.dismiss();
    } catch (err) {
      this.loadingCtrl.dismiss();
      this.msgSrvc.show(`Whoops! An error occurred: ${err.message}`);
    }
  }

  selectCategory(categoryType) {
    this.category = categoryType;
    const queryParams: any = {
      type: categoryType,
    };

    this.navCtrl.navigateRoot([], {
      queryParams,
      queryParamsHandling: 'merge',
    });
    this.load();
  }

  toggleInstalledFilter() {
    this.showInstalledOnly = !this.showInstalledOnly;
    this.load();
  }

  upgradeAccount() {
    this.navCtrl.navigateForward('/upgrade');
  }

  async disableIntegration(app: any) {
    const currentIntegrationSettings = this.authService.tenant.integrations[app.key] ?? {};
    this.isSavingIntegration = true;
    const nangoIntegrationId = currentIntegrationSettings.nangoIntegrationId ?? app.key;

    try {
      await this.apiService.postPromise(`/integrations/disable`, {
        integration: nangoIntegrationId,
        integrationType: app.type,
        isNango: app.isNango,
      });

      await this.authService.loadCompany();

      this.msgSrvc.show('Success!');
    } catch (err: any) {
      this.apiService.postPromise('/log-entries', {
        level: 'error',
        data: {
          message: err.message,
          stack: err.stack,
          code: err.code,
        },
      });

      this.msgSrvc.showError(err);
    } finally {
      this.isSavingIntegration = false;
    }
  }

  async openIntegrationOptions(app: any, event) {
    const DISABLE_INTEGRATION = 'Disable';
    const REAUTHORIZE_INTEGRATION = 'Reauthorize';
    const CHANGE_SETTINGS = 'Manage integration settings';

    const options: ActionDropdownType[] = [];

    options.push({ label: CHANGE_SETTINGS });
    if (app.isNango) {
      options.push({ label: REAUTHORIZE_INTEGRATION });
    }
    options.push({ label: DISABLE_INTEGRATION });

    const popover = await this.popoverCtrl.create({
      component: ActionDropdownComponent,
      componentProps: {
        options,
        callback: async (_index: number, label: string) => {
          if (label === DISABLE_INTEGRATION) {
            await this.disableIntegration(app);
          } else if (label === REAUTHORIZE_INTEGRATION) {
            await this.reauthorizeApp(app);
          } else if (label === CHANGE_SETTINGS) {
            this.requestAccess(app);
          }

          popover.dismiss();
        },
      },
      showBackdrop: false,
      event,
    });

    popover.present();
  }
}
